import { useCallback, useRef } from "react";

export type UseGuardedAsyncCallbackOptions = {
  /** Cooldown (ms) after a run finishes before another invocation is allowed. */
  cooldownMs?: number;
};

type GuardState = {
  inFlight: boolean;
  lastReleasedAt: number;
};

/**
 * Wraps an async click handler: blocks re-entry while running and during a short
 * post-run cooldown so rapid double-clicks cannot fire duplicate API calls.
 */
export function useGuardedAsyncCallback<T extends (...args: any[]) => Promise<void>>(
  callback: T,
  options: UseGuardedAsyncCallbackOptions = {},
): T {
  const { cooldownMs = 300 } = options;
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const guardRef = useRef<GuardState>({ inFlight: false, lastReleasedAt: 0 });

  return useCallback(
    async (...args: Parameters<T>) => {
      const guard = guardRef.current;
      const now = Date.now();
      if (guard.inFlight) return;
      if (cooldownMs > 0 && now - guard.lastReleasedAt < cooldownMs) return;

      guard.inFlight = true;
      try {
        await callbackRef.current(...args);
      } finally {
        guard.inFlight = false;
        guard.lastReleasedAt = Date.now();
      }
    },
    [cooldownMs],
  ) as T;
}
